import { useState, useEffect } from 'react';
import { getTreatments, addTreatment, deleteTreatment } from '../../firebase/treatmentService';
import { hasValidConsent } from '../../firebase/consentService';
import { getActiveStaff } from '../../firebase/staffService';

function TreatmentLog({ clientId }) {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [recordType, setRecordType] = useState('hair');
  const [staffList, setStaffList] = useState([]);
  const [newTreatment, setNewTreatment] = useState({
    date: new Date().toISOString().split('T')[0],
    treatment: '',
    samplesGiven: '',
    productBought: '',
    staffName: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTreatments();
    loadStaff();
  }, [clientId]);

  const loadTreatments = async () => {
    setLoading(true);
    const data = await getTreatments(clientId);
    setTreatments(data);
    setLoading(false);
  };

  const loadStaff = async () => {
    const staff = await getActiveStaff();
    setStaffList(staff);
  };

  const checkConsent = async () => {
    const isValid = await hasValidConsent(clientId, recordType);
    if (!isValid) {
      alert(`Cannot add ${recordType} treatment. Client consent is missing or expired.`);
      return false;
    }
    return true;
  };

  const handleAddTreatment = async () => {
    if (!newTreatment.treatment || !newTreatment.staffName) {
      alert('Please fill in treatment and staff name');
      return;
    }
    const consentOk = await checkConsent();
    if (!consentOk) return;
    setSaving(true);
    await addTreatment(clientId, recordType, newTreatment);
    await loadTreatments();
    setShowForm(false);
    setNewTreatment({
      date: new Date().toISOString().split('T')[0],
      treatment: '',
      samplesGiven: '',
      productBought: '',
      staffName: ''
    });
    setSaving(false);
  };

  const handleDeleteTreatment = async (treatmentId) => {
    if (window.confirm('Delete this treatment record?')) {
      await deleteTreatment(treatmentId);
      await loadTreatments();
    }
  };

  if (loading) return <div className="text-center py-4">Loading treatments...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Treatment History</h3>
        <button onClick={() => setShowForm(!showForm)} className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm">
          + Add Treatment
        </button>
      </div>
      
      {showForm && (
        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-medium mb-3">New Treatment</h4>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Record Type</label>
            <select value={recordType} onChange={(e) => setRecordType(e.target.value)} className="px-3 py-1 border rounded-md">
              <option value="hair">Hair Service</option>
              <option value="skin">Skin Service</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input type="date" value={newTreatment.date} onChange={(e) => setNewTreatment({...newTreatment, date: e.target.value})} className="w-full px-3 py-1 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Staff Name</label>
              <select value={newTreatment.staffName} onChange={(e) => setNewTreatment({...newTreatment, staffName: e.target.value})} className="w-full px-3 py-1 border rounded-md">
                <option value="">Select staff</option>
                {staffList.map(staff => (
                  <option key={staff.id} value={staff.name}>{staff.name} ({staff.type})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
            <input type="text" placeholder="e.g., Full colour, Chemical peel" value={newTreatment.treatment} onChange={(e) => setNewTreatment({...newTreatment, treatment: e.target.value})} className="w-full px-3 py-1 border rounded-md" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Samples Given</label>
              <input type="text" placeholder="Products given" value={newTreatment.samplesGiven} onChange={(e) => setNewTreatment({...newTreatment, samplesGiven: e.target.value})} className="w-full px-3 py-1 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Bought</label>
              <input type="text" placeholder="Products purchased" value={newTreatment.productBought} onChange={(e) => setNewTreatment({...newTreatment, productBought: e.target.value})} className="w-full px-3 py-1 border rounded-md" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setShowForm(false)} className="px-3 py-1 bg-gray-300 rounded-md text-sm">Cancel</button>
            <button onClick={handleAddTreatment} disabled={saving} className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
      
      {treatments.length === 0 ? (
        <p className="text-gray-500 text-center py-4">No treatment records found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead className="bg-gray-100">
              <tr><th className="px-3 py-2 text-left text-sm">Date</th><th className="px-3 py-2 text-left text-sm">Type</th><th className="px-3 py-2 text-left text-sm">Treatment</th><th className="px-3 py-2 text-left text-sm">Staff</th><th className="px-3 py-2 text-left text-sm">Samples</th><th className="px-3 py-2 text-left text-sm">Products</th><th className="px-3 py-2 text-center text-sm">Action</th></tr>
            </thead>
            <tbody>
              {treatments.map((t) => (
                <tr key={t.treatmentId} className="border-t">
                  <td className="px-3 py-2 text-sm">{new Date(t.date).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-sm">{t.recordType === 'hair' ? '💇 Hair' : '💆 Skin'}</td>
                  <td className="px-3 py-2 text-sm">{t.treatment}</td>
                  <td className="px-3 py-2 text-sm">{t.staffName}</td>
                  <td className="px-3 py-2 text-sm">{t.samplesGiven || '-'}</td>
                  <td className="px-3 py-2 text-sm">{t.productBought || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => handleDeleteTreatment(t.treatmentId)} className="text-red-500 hover:text-red-700 text-sm">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TreatmentLog;